/* eslint-disable react/jsx-filename-extension */
import App from 'next/app';
import '../../index.css';
import React, { useEffect, useMemo } from 'react';
import { ApolloProvider } from '@apollo/client';
import { Roboto } from 'next/font/google';
import { useRouter } from 'next/router';
import Head from 'next/head';
import NProgress from 'nprogress';
import PropTypes from 'prop-types';
import { getServerSideToken, clearServerSideAuthToken } from '../utils/auth-utils';
import getServerSideRemoteConfig from '../lib/ssr-firebase-server';
import { FirebaseProvider } from '../contexts/ssr-firebase-context';
import { initializeApollo } from '../../apollo-ssr';
import GET_CURRENT_USER from '../gql/queries/get-useraccount-info.gql';
import HeaderProvider from '../contexts/headerContext';
import { UserProvider } from '../contexts/ssr-user-context';
import { MiscellaneousDataProvider } from '../contexts/ssr-miscellaneous-context';
import { ToastContextProvider } from '../contexts/toast-context-with-provider';
import GoogleTagManager from '../components/google-tag-manager';
import '@madeeasy/universal-login/dist/main.css'; // needed if built in the the production mode - style loader
// eslint-disable-next-line import/no-unresolved
import 'nprogress/nprogress.css';
import ScrollToTop from '../components/shared/ScrollToTop';
import '../components/aws-vod/shaka-player-custom.css';
import '../components/vdocipher/vdocipher-player.css';

import {
  initializeAnalytics, onUserLogin, setAnalyticsUser, updateCleverTapProfile,
} from '../utils/analytics';
import UtmSyncOnLogin from '../components/utm/UtmSyncOnLogin';
import VerificationCheck from '../components/auth/VerificationCheck';
import {
  persistAttributionFromUrlSearch,
  isAttributionEligibleHost,
} from '../utils/marketing-attribution';
import Favicon from '../components/shared/favicon';
import NavigationProvider from '../contexts/navigation-context';

// Optional: Configure NProgress
NProgress.configure({ showSpinner: false });
const roboto = Roboto({ subsets: ['latin'], weight: ['300', '400', '500', '700', '900'] });
function MyApp({ Component, pageProps }) {
  const router = useRouter();

  // const orgProperties = getOrganizationPropertiesByHost(pageProps?.hostname);

  // useFavicon(orgProperties?.favIcon);

  useEffect(() => {
    const handleRuntimeError = (event) => {
      const message = event.message?.toString().slice(0, 100);
      const stack = event.stack?.toString().slice(0, 100);

      console.log('client_js_error', message, stack);
      // Optional: send to your logging/monitoring service
    };

    const handleUnhandledRejection = (event) => {
      const message = event.reason?.message?.toString().slice(0, 100)
        || 'Unhandled Promise Rejection';
      const stack = event.reason?.stack?.toString().slice(0, 100) || 'N/A';

      console.log('client_js_unhandled_rejection', message, stack);
      // Optional: send to your logging/monitoring service
    };

    window.addEventListener('error', handleRuntimeError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleRuntimeError);
      window.removeEventListener(
        'unhandledrejection',
        handleUnhandledRejection,
      );
    };
  }, []);

  useEffect(() => {
    const handleLogin = (event) => {
      const { user } = event.detail;
      setAnalyticsUser(user);
      onUserLogin(user);
    };

    window.addEventListener('loginSuccess', handleLogin);

    return () => {
      window.removeEventListener('loginSuccess', handleLogin);
    };
  }, []);

  useEffect(() => {
    const init = async () => {
      await initializeAnalytics();

      if (pageProps.user?.me) {
        setAnalyticsUser(pageProps.user?.me);
        updateCleverTapProfile(pageProps.user?.me);
      }
    };

    init();
  }, []);

  useEffect(() => {
    const handleStart = () => NProgress.start();
    const handleComplete = () => NProgress.done();

    router.events.on('routeChangeStart', handleStart);
    router.events.on('routeChangeComplete', handleComplete);
    router.events.on('routeChangeError', handleComplete);

    return () => {
      router.events.off('routeChangeStart', handleStart);
      router.events.off('routeChangeComplete', handleComplete);
      router.events.off('routeChangeError', handleComplete);
    };
  }, [router]);

  // Initialize Apollo Client on client-side with the cached state
  const apolloClient = useMemo(
    () => initializeApollo(pageProps.apolloState, pageProps.userToken, pageProps?.hostname),
    [pageProps.apolloState, pageProps.userToken, pageProps?.hostname],
  );

  useEffect(() => {
    const hostname = typeof window !== 'undefined' ? window.location.hostname : pageProps?.hostname;
    if (!isAttributionEligibleHost(hostname)) return;

    persistAttributionFromUrlSearch({ hostname });
  }, [router.asPath, pageProps?.hostname]);
  const getLayout = Component.getLayout ?? ((page) => page);
  return (
    <>
      <Head>
        <meta key="robots" name="robots" content="noindex, nofollow" />
      </Head>
      <ApolloProvider client={apolloClient}>
        <FirebaseProvider
          initialConfig={pageProps.firebaseConfig}
          user={pageProps.user?.me}
        >
          <MiscellaneousDataProvider
            initialData={{ hostname: pageProps?.hostname || '', isPrepPrefixed: pageProps?.isPrepPrefixed || false }}
          >
            <UserProvider
              initialUser={pageProps.user?.me}
              userToken={pageProps.userToken}
            >
              <Favicon />
              <NavigationProvider>
                <HeaderProvider>
                  <ToastContextProvider>
                    <GoogleTagManager />
                    <VerificationCheck />
                    <main className={roboto.className}>
                      <UtmSyncOnLogin />
                      <ScrollToTop />
                      {/*  eslint-disable-next-line react/jsx-props-no-spreading */}
                      {getLayout(<Component {...pageProps} />)}
                    </main>
                  </ToastContextProvider>
                </HeaderProvider>
              </NavigationProvider>
            </UserProvider>
          </MiscellaneousDataProvider>
        </FirebaseProvider>
      </ApolloProvider>
    </>
  );
}

MyApp.propTypes = {
  Component: PropTypes.elementType.isRequired,
  pageProps: PropTypes.object.isRequired,
};

MyApp.getInitialProps = async (appContext) => {
  const { ctx } = appContext;
  const appProps = await App.getInitialProps(appContext);

  let apolloState = null;
  let user = null;
  let userToken = null;
  let firebaseConfig = {};
  let hostname;
  let isPrepPrefixed = false;
  if (ctx && ctx.req && ctx.req.headers && ctx.req.headers.host) {
    hostname = ctx.req.headers.host;
  } else {
    // Fallback if no context provided
    hostname = 'nextias.com';
  }
  // Adjust for atlas subdomain -- Load balancer preserves host header
  if (hostname.startsWith('atlas.')) {
    hostname = 'www.madeeasy.in';
  }

  isPrepPrefixed = process.env.NEXT_PUBLIC_USE_BASE_PATH === 'true';
  console.log('isPrepPrefixed from env', isPrepPrefixed, process.env.NEXT_PUBLIC_USE_BASE_PATH);

  // Only run on server-side
  if (typeof window === 'undefined') {
    try {
      // Parse user token from cookies
      userToken = getServerSideToken(ctx.req);
      // console.log('[SSR] User Token:', userToken);

      // Initialize Apollo Client
      const apolloClient = initializeApollo(null, userToken, hostname);

      // Fetch user data and Firebase config in parallel
      const [userResult, configResult] = await Promise.allSettled([
        // Fetch user if token exists
        userToken ? apolloClient.query({
          query: GET_CURRENT_USER,
          skip: !userToken,
          errorPolicy: 'ignore',
          fetchPolicy: 'network-only',
        }) : Promise.resolve(null),

        // Fetch Firebase Remote Config (server-side)
        getServerSideRemoteConfig(hostname),
      ]);

      // Process user result
      if (userResult.status === 'fulfilled' && userResult.value) {
        user = userResult.value.data || null;
        console.log('[SSR] User data fetched:', user ? 'Success' : 'No user');
      } else if (userResult.status === 'rejected' && userToken) {
        // If user fetch failed but we had a token, it's likely expired/invalid
        console.warn('[SSR] User fetch failed with token present, clearing cookie:', userResult.reason?.message);
        if (ctx.res) {
          clearServerSideAuthToken(ctx.res);
          userToken = null; // Reset token in response
        }
      }

      // Process Firebase config result
      if (configResult.status === 'fulfilled' && configResult.value) {
        firebaseConfig = configResult.value;
        console.log('[SSR] Firebase config fetched:', Object.keys(firebaseConfig).length, 'keys');
      }

      // Extract Apollo cache
      apolloState = apolloClient.cache.extract();
    } catch (error) {
      console.error('[SSR] Error in getInitialProps:', error);
    }
  }

  return {
    ...appProps,
    pageProps: {
      ...appProps.pageProps,
      apolloState,
      user,
      userToken,
      firebaseConfig,
      hostname,
      isPrepPrefixed,
    },
  };
};

export default MyApp;
